const SCROLLABLE_OVERFLOW = /^(auto|scroll|overlay)$/;

const OVERFLOW_EPSILON = 1;

function isScrollableIn(
  el: Element,
  axis: 'y' | 'x',
  style: CSSStyleDeclaration,
): boolean {
  const overflow = axis === 'y' ? style.overflowY : style.overflowX;
  if (!SCROLLABLE_OVERFLOW.test(overflow)) return false;
  const scrollSize = axis === 'y' ? el.scrollHeight : el.scrollWidth;
  const clientSize = axis === 'y' ? el.clientHeight : el.clientWidth;
  return scrollSize - clientSize > OVERFLOW_EPSILON;
}

function canMove(el: Element, axis: 'y' | 'x', delta: number): boolean {
  const pos = axis === 'y' ? el.scrollTop : el.scrollLeft;
  const scrollSize = axis === 'y' ? el.scrollHeight : el.scrollWidth;
  const clientSize = axis === 'y' ? el.clientHeight : el.clientWidth;
  if (delta > 0) return pos + clientSize < scrollSize - OVERFLOW_EPSILON;
  if (delta < 0) return pos > OVERFLOW_EPSILON;
  return true;
}

function containsOverscroll(style: CSSStyleDeclaration, axis: 'y' | 'x'): boolean {
  const value =
    (axis === 'y' ? style.overscrollBehaviorY : style.overscrollBehaviorX) ||
    style.overscrollBehavior;
  return value === 'contain' || value === 'none';
}

export function hasScrollableAncestorWithin(
  target: EventTarget | null,
  boundary: Element | null,
  deltaY: number,
  deltaX = 0,
): boolean {
  if (!boundary || !(target instanceof Element)) return false;
  if (!boundary.contains(target)) return false;

  let node: Element | null = target;
  while (node) {
    let style: CSSStyleDeclaration | null = null;
    try {
      style = window.getComputedStyle(node);
    } catch {
      style = null;
    }

    if (style) {
      const axes: Array<['y' | 'x', number]> = [
        ['y', deltaY],
        ['x', deltaX],
      ];
      for (const [axis, delta] of axes) {
        if (delta === 0 && !(axis === 'y' && deltaX === 0)) continue;
        if (!isScrollableIn(node, axis, style)) continue;
        if (canMove(node, axis, delta)) return true;
        if (containsOverscroll(style, axis)) return false;
      }
    }

    if (node === boundary) break;
    node = node.parentElement;
  }

  return false;
}

export interface NestedScrollGuardHandlers {
  onWheelCapture: (event: React.WheelEvent) => void;
  onTouchMoveCapture: (event: React.TouchEvent) => void;
}

export function createNestedScrollGuard(): NestedScrollGuardHandlers {
  return {
    onWheelCapture: (event) => {
      if (
        hasScrollableAncestorWithin(
          event.target,
          event.currentTarget as Element,
          event.deltaY,
          event.deltaX,
        )
      ) {
        event.stopPropagation();
      }
    },
    onTouchMoveCapture: (event) => {
      if (hasScrollableAncestorWithin(event.target, event.currentTarget as Element, 0, 0)) {
        event.stopPropagation();
      }
    },
  };
}
